import {
  Article,
  ArticleWithStoreStock,
  SizeWithStores,
  Store,
  TrackingData,
} from '../../../@types/online-reservation.js';

export interface ArticleResponseDTO {
  brand: string;
  name: string;
  color?: string;
  price: {
    value: string;
    priceReduced: boolean;
  };
  image: {
    src: string;
    alt?: string;
  };
}

export interface TrackingDataDTO {
  produktId: string;
  farbId?: string;
  syan: string;
}

export interface OnlineReservationResponseDTO {
  article: ArticleResponseDTO;
  sizesWithStoreStock?: ArticleWithStoreStock[];
  stores?: SizeWithStores[];
  skipSizeLayer?: boolean;
  selectedSizeId?: string | null;
  trackingData: TrackingDataDTO;
}

export class ModelState {
  public readonly article: Article;
  public readonly sizesWithStoreStock: ArticleWithStoreStock[];
  public readonly sizesWithStores: SizeWithStores[];
  public readonly skipSizeLayer: boolean;
  public readonly trackingData: TrackingData;
  public readonly selectedSizeId?: string | null;

  private static readonly EMPTY_ARTICLE: Article = {
    brand: '',
    name: '',
    color: '',
    price: {
      value: '',
      priceReduced: false,
    },
    image: {
      src: '',
      alt: '',
    },
  };

  private static readonly EMPTY_TRACKINGDATA: TrackingData = {
    produkt_id: '',
    farb_id: '',
    syan: '',
  };

  constructor(
    article: Article = ModelState.EMPTY_ARTICLE,
    sizesWithStoreStock: ArticleWithStoreStock[] = [],
    sizesWithStores: SizeWithStores[] = [],
    skipSizeLayer = false,
    trackingData = ModelState.EMPTY_TRACKINGDATA,
    selectedSizeId?: string | null,
  ) {
    this.article = article;
    this.sizesWithStoreStock = sizesWithStoreStock;
    this.sizesWithStores = sizesWithStores;
    this.skipSizeLayer = skipSizeLayer;
    this.trackingData = trackingData;
    this.selectedSizeId = selectedSizeId;
  }

  static createFromResponse(response: OnlineReservationResponseDTO): ModelState {
    const article = response.article;
    const price = article.price;
    const image = article.image;
    const imageAltText = `${article.brand} ${article.name}${article.color ? ': ' + article.color : ''}`;

    const updatedArticle = {
      brand: article.brand,
      name: article.name,
      color: article.color,
      price: {
        value: price.value,
        priceReduced: price.priceReduced,
      },
      image: {
        src: image.src,
        alt: imageAltText,
      },
    };

    const trackingData =
      typeof response.trackingData === 'object' && 'produktId' in response.trackingData
        ? {
            produkt_id: response.trackingData.produktId,
            farb_id: response.trackingData.farbId,
            syan: response.trackingData.syan,
          }
        : { produkt_id: '', farb_id: undefined, syan: '' };

    return new ModelState(
      updatedArticle,
      response.sizesWithStoreStock ?? [],
      response.stores ?? [],
      response.skipSizeLayer ?? false,
      trackingData,
      response.selectedSizeId ?? undefined,
    );
  }

  public getSizeById(sizeId: string): string | undefined {
    const size = this.sizesWithStoreStock.find((sizeWithStores) => sizeWithStores.id === sizeId);
    return size?.value;
  }

  public getsalesInformationIdById(sizeId: string): string | undefined {
    const size = this.sizesWithStoreStock.find((sizeWithStores) => sizeWithStores.id === sizeId);
    return size?.salesInformationId;
  }

  public getStoresForSize(sizeId: string): Store[] {
    const sizeWithStores = this.sizesWithStores.find((item) => item.article === sizeId);
    return sizeWithStores?.stores || [];
  }

  public shouldSkipToStoreSelection(): boolean {
    return this.skipSizeLayer && this.sizesWithStoreStock.length > 0;
  }

  public getFirstAvailableSize(): string | undefined {
    return this.sizesWithStoreStock.length > 0 ? this.sizesWithStoreStock[0].id : undefined;
  }
}
